import { ReactElement } from 'react';

import { DateTime, DateTimeFormatOptions } from 'luxon';
import { useIntl } from 'react-intl';
import classNames from 'classnames';

import { useAppSelector } from 'hooks';
import { getRangeColor } from 'utils/Icons';
import { getFormattedMeasurementRecord } from 'utils/MeasurementsStoreFunctions';
import { CustomBadge, IconTooltip } from 'components/UI';
import {
  BPEvents,
  extractViaInfo,
  getMeasurementErrors,
  measurementErrorLabelKeys,
  measurementErrorTooltipKeys
} from 'utils/Measurements';
import { irregularHeartBeat, measurementError, tricheck } from 'assets';
import OverlayTooltip from 'components/Forms/OverlayTooltip';
import { getCurrentOrg } from 'store/groups/reducer';

interface ILastMeasurementProps {
  record: IMeasurementRecord;
  inline?: boolean;
  dateFirst?: boolean;
  dateFormat?: DateTimeFormatOptions;
  badgeWrapper?: (badge: ReactElement) => ReactElement;
}

const LastMeasurement = ({
  record,
  inline,
  dateFirst,
  dateFormat = DateTime.DATETIME_SHORT,
  badgeWrapper
}: ILastMeasurementProps) => {
  const intl = useIntl();
  const locale = useAppSelector((state) => state.intl.locale);
  const org = useAppSelector(getCurrentOrg);
  const formattedTime = record.measure_date.replace(' ', 'T') + 'Z';
  const via_split = record.via.split(';');

  const event = extractViaInfo(via_split, 'event');
  const ihb = extractViaInfo(via_split, 'ihb');

  const errors = getMeasurementErrors(record);

  const measurementFragment = (
    <span data-test={`measurement-${record.recordId}-value`}>
      {getFormattedMeasurementRecord(record, intl)}
    </span>
  );

  const dateFragment = (
    <small
      data-test={`measurement-${record.recordId}-time`}
      data-test-value={DateTime.fromSQL(record.measure_date).toMillis()}
      className={classNames('pt-2 text-center text-gray-550', {
        'd-block': !inline,
        'ms-3': inline && !dateFirst,
        'me-3': inline && dateFirst
      })}
    >
      {DateTime.fromISO(formattedTime).setLocale(locale).toLocaleString(dateFormat)}
    </small>
  );

  const iconFragment = (
    <div className="ms-1">
      {event === BPEvents.Average.toString() && (
        <OverlayTooltip message="tricheckTooltipKey">
          <img src={tricheck} height={14} />
        </OverlayTooltip>
      )}
      {ihb === 'true' && !!org?.irregularHeartbeatEnabled && (
        <OverlayTooltip message="irregularHeartbeatTooltipKey">
          <img src={irregularHeartBeat} height={14} />
        </OverlayTooltip>
      )}
      {errors.map((error) => (
        <IconTooltip
          key={error}
          dataTestId={`measurement-${record.recordId}-error-${error}`}
          overlay={
            <>
              <div className="font-weight-bold">
                {intl.formatMessage({ id: measurementErrorLabelKeys[error] })}
              </div>
              {intl.formatMessage({ id: measurementErrorTooltipKeys[error] })}
            </>
          }
        >
          <img
            data-test={`measurement-${record.recordId}-error-${error}`}
            src={measurementError}
            height={14}
          />
        </IconTooltip>
      ))}
    </div>
  );

  const firstFragment = dateFirst ? dateFragment : measurementFragment;
  const secondFragment = dateFirst ? measurementFragment : dateFragment;

  const color = getRangeColor(record.color_6000_name);
  const badge = (
    <CustomBadge data-test={`measurement-${record.recordId}`} color={color}>
      {firstFragment}
      {secondFragment}
    </CustomBadge>
  );
  return (
    <div className="d-flex flex-direction-row align-items-center">
      {badgeWrapper ? badgeWrapper(<div className="d-flex">{badge}</div>) : badge}
      {iconFragment}
    </div>
  );
};

export default LastMeasurement;
